import React, { useEffect, useMemo, useReducer, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Flex,
  Text,
  Button,
  Spinner,
  Icon,
  useToast,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiLink2, FiRefreshCw, FiExternalLink } from 'react-icons/fi';
import { reducer, init, getReservedOrdinalsExcludingCard, getReservedMonthsExcludingCard } from './linkPaymentsReducer';
import { useLinkPaymentsData } from './useLinkPaymentsData';
import { MONTHS } from './constants';
import ContextBanner from './ContextBanner';
import SummaryBar from './SummaryBar';
import PaymentCard from './PaymentCard';
import PostSaveSummary from './PostSaveSummary';

/**
 * Modal unificado para vincular pagos huérfanos a su matrícula y asignar
 * ordinales (semester-based) o meses (anualizado).
 *
 * Props:
 *  - isOpen, onClose  (control de Chakra)
 *  - studentId, programId (opcionales — si vienen resuelven Modo A)
 *  - enrollmentType: 'grade' | 'technical_career'
 *  - enrollmentId (opcional — Modo B)
 *  - studentName (para el banner)
 *  - programName (para el banner)
 *  - onSaved(savedCount): callback tras batch exitoso
 */
export default function LinkPaymentsModal({
  isOpen,
  onClose,
  studentId,
  programId,
  enrollmentType,
  enrollmentId,
  studentName,
  programName,
  semesterName,
  anchorAuto,
  onSaved,
}) {
  const toast = useToast();
  const [state, dispatch] = useReducer(reducer, undefined, init);
  const { fetchCandidates, saveBatch, unlinkPayment, setAnchorOverride } = useLinkPaymentsData();
  const [unlinkingId, setUnlinkingId] = React.useState(null);

  // Mapa 1..12 → monthId (identidad — MONTHS_CATALOG usa id === número).
  const monthIdByNumber = useMemo(() => {
    const m = new Map();
    for (const item of MONTHS) m.set(item.number, item.number);
    return m;
  }, []);

  // Fetch inicial al abrir.
  const loadData = useCallback(async () => {
    dispatch({ type: 'LOAD_START' });
    try {
      const { enrollments, payments, meta, claimedOrdinalsByEnrollment } = await fetchCandidates({
        enrollmentType,
        enrollmentId,
        studentId,
        programId,
      });
      dispatch({ type: 'LOAD_SUCCESS', payload: { enrollments, payments, meta, claimedOrdinalsByEnrollment } });
    } catch (err) {
      console.error('LinkPaymentsModal load:', err);
      dispatch({ type: 'LOAD_ERROR', error: err?.message || 'Error al cargar' });
    }
  }, [fetchCandidates, enrollmentType, enrollmentId, studentId, programId]);

  useEffect(() => {
    if (isOpen) loadData();
    else dispatch({ type: 'RESET' });
  }, [isOpen, loadData]);

  const eligibleCards = useMemo(() => {
    return state.order
      .map((id) => state.cards[id])
      .filter((c) => c && c.included && c.status !== 'saved');
  }, [state.order, state.cards]);

  const cardsList = useMemo(() => state.order.map((id) => state.cards[id]).filter(Boolean), [state.order, state.cards]);
  const pendingCards = useMemo(() => cardsList.filter((c) => c.status === 'pending'), [cardsList]);
  const savedCount = useMemo(() => cardsList.filter((c) => c.status === 'saved').length, [cardsList]);
  const failedCount = useMemo(() => cardsList.filter((c) => c.status === 'failed').length, [cardsList]);

  const canSave = useMemo(() => {
    if (state.phase !== 'ready' && state.phase !== 'done') return false;
    if (eligibleCards.length === 0) return false;
    // Cada card elegible necesita enrollmentId + (ordinals o skipOrdinals para semester-based) o months (anualizado).
    return eligibleCards.every((c) => {
      if (!c.enrollmentId) return false;
      const enrollment = state.enrollments.find((e) => e.id === c.enrollmentId);
      if (!enrollment) return false;
      if (enrollment.isSemesterBased) return c.ordinals.length > 0 || c.skipOrdinals;
      return c.months.length > 0;
    });
  }, [state.phase, state.enrollments, eligibleCards]);

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    dispatch({ type: 'BEGIN_PERSIST' });
    const items = eligibleCards.map((c) => {
      const enrollment = state.enrollments.find((e) => e.id === c.enrollmentId);
      const isSem = enrollment?.isSemesterBased === true;
      const base = { paymentId: c.payment.id, enrollmentType, enrollmentId: c.enrollmentId };
      if (isSem) {
        return c.skipOrdinals
          ? { ...base, skipOrdinals: true }
          : { ...base, paidSubjectNumbers: c.ordinals };
      }
      return { ...base, months: c.months };
    });
    try {
      const { saved, failed } = await saveBatch(items);
      const savedIds = new Set(saved.map(Number));
      for (const c of eligibleCards) {
        if (savedIds.has(c.payment.id)) {
          dispatch({ type: 'PERSIST_ONE_SUCCESS', paymentId: c.payment.id });
        }
      }
      for (const f of failed) {
        dispatch({
          type: 'PERSIST_ONE_FAILURE',
          paymentId: f.paymentId,
          code: f.code,
          message: f.message,
          conflict: f.conflict,
        });
      }
      dispatch({ type: 'PERSIST_DONE' });
      if (saved.length > 0) {
        toast({
          title: `${saved.length} pago${saved.length === 1 ? '' : 's'} vinculado${saved.length === 1 ? '' : 's'}`,
          status: 'success',
          position: 'bottom',
        });
        if (onSaved) onSaved(saved.length);
      }
      if (failed.length === 0) {
        // Auto-cerrar tras un delay para dar tiempo a leer el summary.
        setTimeout(() => onClose && onClose(), 800);
      }
    } catch (err) {
      console.error('LinkPaymentsModal save batch:', err);
      toast({
        title: 'Error al guardar el batch',
        description: err?.message || 'Intenta nuevamente',
        status: 'error',
        position: 'bottom',
      });
      // Marcar todos los pending como failed genéricos.
      for (const c of eligibleCards) {
        dispatch({
          type: 'PERSIST_ONE_FAILURE',
          paymentId: c.payment.id,
          code: 'INTERNAL',
          message: 'Error de red',
        });
      }
      dispatch({ type: 'PERSIST_DONE' });
    }
  }, [canSave, eligibleCards, state.enrollments, enrollmentType, saveBatch, toast, onClose, onSaved]);

  const handleRetryFailed = useCallback(() => {
    dispatch({ type: 'RESET_FAILED_ONLY' });
  }, []);

  const handleUnlink = useCallback(async (paymentId) => {
    setUnlinkingId(paymentId);
    try {
      // Multi-programa: solo limpia el link de la matrícula específica del card,
      // no todos los links del pago. El backend cae en compat-legacy si no se pasan.
      const card = state.cards[paymentId];
      const opts = card?.enrollmentId
        ? { enrollmentType, enrollmentId: card.enrollmentId }
        : {};
      await unlinkPayment(paymentId, opts);
      dispatch({ type: 'UNLINK_PAYMENT', paymentId });
      toast({ title: `Pago #${paymentId} desvinculado`, status: 'info', position: 'bottom' });
      if (onSaved) onSaved(0); // refresca contador del reporte
    } catch (err) {
      toast({
        title: 'No se pudo desvincular',
        description: err?.response?.data?.error?.message || err?.message,
        status: 'error',
        position: 'bottom',
      });
    } finally {
      setUnlinkingId(null);
    }
  }, [unlinkPayment, toast, onSaved, state.cards, enrollmentType]);

  const handleAnchorChange = useCallback(async () => {
    // Al cambiar el ancla, recargamos el modal para reflejar overrideAt/By actuales.
    await loadData();
    if (onSaved) onSaved(0);
  }, [loadData, onSaved]);

  const isSaving = state.phase === 'saving';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent maxW="900px">
        <ModalHeader borderBottomWidth="1px" borderColor="gray.100" py={4}>
          <Flex align="center" justify="space-between" gap={3}>
            <Box>
              <Flex align="center" gap={2}>
                <Icon as={FiLink2} color="#574feb" boxSize="18px" />
                <Text fontSize="16px" fontWeight="700">
                  Vincular pagos{studentName ? ` — ${studentName}` : ''}
                </Text>
              </Flex>
              {(programName || semesterName) && (
                <Text fontSize="12px" color="gray.500" fontWeight="500" mt={0.5}>
                  {programName}
                  {programName && semesterName ? ' · ' : ''}
                  {semesterName ? `Semestre ${semesterName}` : ''}
                </Text>
              )}
            </Box>
            {studentId && (
              <Box
                as="a"
                href={`/plataforma/administrador/ver-estudiante/${studentId}`}
                target="_blank"
                rel="noopener noreferrer"
                fontSize="12px"
                color="#574feb"
                display="inline-flex"
                alignItems="center"
                gap={1}
                mr={8}
                fontWeight="600"
                _hover={{ textDecoration: 'underline' }}
              >
                Ver perfil <Icon as={FiExternalLink} boxSize="11px" />
              </Box>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton top={4} />

        {state.phase === 'loading' && (
          <ModalBody py={12}>
            <Flex direction="column" align="center" gap={3}>
              <Spinner size="lg" color="#574feb" />
              <Text fontSize="sm" color="gray.500">
                Cargando pagos por vincular…
              </Text>
            </Flex>
          </ModalBody>
        )}

        {state.phase === 'error' && (
          <ModalBody py={6}>
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box flex="1">
                <Text fontSize="sm" fontWeight="600">
                  No se pudieron cargar los pagos
                </Text>
                <Text fontSize="xs" color="gray.600" mt={1}>
                  {state.error}
                </Text>
              </Box>
              <Button
                size="sm"
                leftIcon={<FiRefreshCw />}
                onClick={loadData}
                variant="outline"
              >
                Reintentar
              </Button>
            </Alert>
          </ModalBody>
        )}

        {(state.phase === 'ready' || state.phase === 'saving' || state.phase === 'done') && (
          <>
            <ContextBanner
              enrollments={state.enrollments}
              orphanCount={pendingCards.length}
              programName={programName}
              studentName={studentName}
              enrollmentType={enrollmentType}
              anchorOverride={state.enrollments[0]?.financialAnchorOverride || null}
              anchorOverrideNote={state.enrollments[0]?.financialAnchorOverrideNote || null}
              anchorOverrideAt={state.enrollments[0]?.financialAnchorOverrideAt || null}
              anchorOverrideBy={state.enrollments[0]?.financialAnchorOverrideBy ?? null}
              anchorAuto={anchorAuto || null}
              onAnchorChange={handleAnchorChange}
              setAnchorOverride={setAnchorOverride}
            />

            {state.enrollments.length === 1 && state.meta.isSemesterBased && (
              <SummaryBar
                visible={cardsList.length > 0}
                includedCount={eligibleCards.length}
                totalCount={cardsList.length}
                claimedOrdinalsList={
                  Array.from(
                    (state.claimedOrdinalsByEnrollment[state.enrollments[0].id] || new Map()).entries()
                  ).map(([ord, meta]) => ({ ord, source: meta?.source || 'payment' }))
                }
                subjectsTotal={state.subjectCount}
              />
            )}

            <ModalBody py={4}>
              {(savedCount > 0 || failedCount > 0) && (
                <PostSaveSummary
                  savedCount={savedCount}
                  failedCount={failedCount}
                  onRetryFailed={handleRetryFailed}
                />
              )}

              {cardsList.length === 0 && (
                <Box textAlign="center" py={8}>
                  <Text fontSize="sm" color="gray.500">
                    No hay pagos registrados en esta matrícula.
                  </Text>
                </Box>
              )}

              <Flex direction="column" gap={3}>
                {cardsList.map((card) => (
                  <PaymentCard
                    key={card.payment.id}
                    onUnlink={handleUnlink}
                    isUnlinking={unlinkingId === card.payment.id}
                    card={card}
                    enrollments={state.enrollments}
                    isSemesterBased={
                      state.enrollments.find((e) => e.id === card.enrollmentId)?.isSemesterBased === true
                    }
                    subjectsTotal={state.subjectCount}
                    reservedOrdinals={getReservedOrdinalsExcludingCard(state, card.payment.id)}
                    reservedMonths={getReservedMonthsExcludingCard(state, card.payment.id)}
                    blockedOrdinalsInDb={state.claimedOrdinalsByEnrollment[card.enrollmentId] || new Map()}
                    monthIdByNumber={monthIdByNumber}
                    onSelectCandidate={(pid, eid) => dispatch({ type: 'SELECT_CANDIDATE', paymentId: pid, enrollmentId: eid })}
                    onToggleOrdinal={(pid, n) => dispatch({ type: 'TOGGLE_ORDINAL', paymentId: pid, ordinal: n })}
                    onToggleMonth={(pid, m) => dispatch({ type: 'TOGGLE_MONTH', paymentId: pid, monthId: m })}
                    onToggleInclude={(pid) => dispatch({ type: 'TOGGLE_INCLUDE', paymentId: pid })}
                    onToggleSkipOrdinals={(pid) => dispatch({ type: 'TOGGLE_SKIP_ORDINALS', paymentId: pid })}
                  />
                ))}
              </Flex>
            </ModalBody>

            <ModalFooter borderTopWidth="1px" borderColor="gray.100" py={3}>
              <Flex justify="flex-end" gap={2} w="100%">
                <Button variant="ghost" size="sm" onClick={onClose} isDisabled={isSaving}>
                  Cerrar
                </Button>
                <Button
                  bg="#574feb"
                  color="white"
                  _hover={{ bg: '#4437c9' }}
                  size="sm"
                  onClick={handleSave}
                  isDisabled={!canSave || isSaving}
                  isLoading={isSaving}
                  loadingText="Guardando…"
                >
                  {eligibleCards.length > 0 ? `Vincular (${eligibleCards.length})` : 'Guardar'}
                </Button>
              </Flex>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
